/**
 * Edge Case Handlers for AstraPOS Game
 * Handles scenarios like validator leaving, no stakes placed, etc.
 */

import {
  arrayUnion,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';

/**
 * Handle validator leaving during PLAYING stage
 * Reassigns to next highest staker
 */
export async function handleMinerLeave(roomId) {
  try {
    const roomRef = doc(db, 'rooms', roomId);
    const roomSnapshot = await getDocs(
      query(collection(db, 'rooms'), where('__name__', '==', roomId))
    );

    if (roomSnapshot.empty) return;

    const roomData = roomSnapshot.docs[0].data();
    const currentCycle = roomData.gameState?.currentCycle;
    const readyPlayers = roomData.readyPlayers || [];

    if (!readyPlayers || readyPlayers.length === 0) {
      console.warn('No ready players available for reassignment');
      return;
    }

    // Find next highest staker
    const stakesSnapshot = await getDocs(
      query(
        collection(db, 'rooms', roomId, 'stakes'),
        where('gameCycle', '==', currentCycle)
      )
    );

    const stakes = stakesSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    // Sort by stake amount (descending) then timestamp (ascending)
    stakes.sort((a, b) => {
      if (b.stakeAmount !== a.stakeAmount) {
        return b.stakeAmount - a.stakeAmount;
      }
      const timeA =
        a.stakedAt?.toDate?.().getTime?.() || a.stakedAt?.getTime?.() || 0;
      const timeB =
        b.stakedAt?.toDate?.().getTime?.() || b.stakedAt?.getTime?.() || 0;
      return timeA - timeB;
    });

    // Find first non-validator staker
    let nextMiner = null;
    for (const stake of stakes) {
      if (!roomData.miners?.includes(stake.playerPublicKey)) {
        nextMiner = stake.playerPublicKey;
        break;
      }
    }

    // Fallback to first ready player if no other staker
    if (!nextMiner && readyPlayers.length > 0) {
      const sortedPlayers = [...readyPlayers].sort((a, b) =>
        a.localeCompare(b)
      );
      nextMiner = sortedPlayers[0];
    }

    if (nextMiner) {
      // Update validators array with new validator
      await updateDoc(roomRef, {
        miners: arrayUnion(nextMiner),
      });

      // Move old validator to participants if needed
      const currentMiners = roomData.miners || [];
      const newMiners = currentMiners.filter((m) => m !== nextMiner);

      console.log(`Validator reassigned to ${nextMiner}`);
    }
  } catch (error) {
    console.error('Error handling validator leave:', error);
  }
}

/**
 * Handle scenario where all eligible players left
 * Gracefully end the game or handle appropriately
 */
export async function handleAllPlayersLeave(roomId) {
  try {
    const roomRef = doc(db, 'rooms', roomId);

    // Update game state to indicate issue
    await updateDoc(roomRef, {
      'gameState.stage': 'WAITING',
      readyPlayers: [],
      miners: [],
      participants: [],
    });

    console.warn('All players left. Game reset to WAITING stage.');
  } catch (error) {
    console.error('Error handling all players leave:', error);
  }
}

/**
 * Validate and handle no-stakes scenario
 * Ensures alphabetical fallback works correctly
 */
export async function validateNoStakesScenario(
  roomId,
  currentCycle,
  readyPlayers
) {
  try {
    // Check if any stakes exist for this cycle
    const stakesSnapshot = await getDocs(
      query(
        collection(db, 'rooms', roomId, 'stakes'),
        where('gameCycle', '==', currentCycle)
      )
    );

    const hasStakes = !stakesSnapshot.empty;

    if (!hasStakes && readyPlayers && readyPlayers.length > 0) {
      // No stakes placed - use alphabetical fallback
      const sortedPlayers = [...readyPlayers].sort((a, b) =>
        a.localeCompare(b)
      );

      console.log(
        `No stakes placed. Assigning Validator role to ${sortedPlayers[0]} (alphabetically first)`
      );

      return {
        publicKey: sortedPlayers[0],
        hasStakes: false,
        message: 'No stakes placed. Winner selected alphabetically.',
      };
    }

    return {
      hasStakes: true,
    };
  } catch (error) {
    console.error('Error validating no-stakes scenario:', error);
    return { error: true };
  }
}

/**
 * Handle late joiners during different game stages
 */
export async function handleLateJoiner(
  roomId,
  playerPublicKey,
  playerEmail,
  currentStage
) {
  try {
    const roomRef = doc(db, 'rooms', roomId);

    switch (currentStage) {
      case 'WAITING':
        // Late joiner during waiting can join normally
        break;

      case 'STAKING':
        // Late joiner during staking - auto add as participant
        await updateDoc(roomRef, {
          participants: arrayUnion(playerPublicKey),
        });
        console.log(
          `Late joiner ${playerEmail} added as Participant during STAKING`
        );
        break;

      case 'PLAYING':
        // Late joiner during playing - auto add as participant
        await updateDoc(roomRef, {
          participants: arrayUnion(playerPublicKey),
        });
        console.log(
          `Late joiner ${playerEmail} added as Participant during PLAYING`
        );
        break;

      case 'LEADERBOARD':
        // Late joiner during leaderboard - add as participant for next cycle
        await updateDoc(roomRef, {
          participants: arrayUnion(playerPublicKey),
        });
        console.log(`Late joiner ${playerEmail} queued for next cycle`);
        break;

      default:
        console.warn(`Unknown stage for late joiner: ${currentStage}`);
    }
  } catch (error) {
    console.error('Error handling late joiner:', error);
  }
}

/**
 * Validate minimum players are still present
 * If minimum not met, prevent stage transitions
 */
export async function validateMinimumPlayers(roomId, minPlayers) {
  try {
    const roomSnapshot = await getDocs(
      query(collection(db, 'rooms'), where('__name__', '==', roomId))
    );

    if (roomSnapshot.empty) return false;

    const roomData = roomSnapshot.docs[0].data();
    const activeParticipants = [
      ...(roomData.miners || []),
      ...(roomData.participants || []),
      ...(roomData.readyPlayers || []),
    ];

    const uniquePlayers = new Set(activeParticipants);

    return uniquePlayers.size >= minPlayers;
  } catch (error) {
    console.error('Error validating minimum players:', error);
    return false;
  }
}

/**
 * Handle disconnect and reconnect scenarios
 * Ensure player role is properly restored
 */
export async function handlePlayerReconnect(roomId, playerPublicKey) {
  try {
    const roomSnapshot = await getDocs(
      query(collection(db, 'rooms'), where('__name__', '==', roomId))
    );

    if (roomSnapshot.empty) return null;

    const roomData = roomSnapshot.docs[0].data();

    // Determine player's current role
    if (roomData.miners?.includes(playerPublicKey)) {
      return 'validator';
    } else if (roomData.participants?.includes(playerPublicKey)) {
      return 'participant';
    }

    return null;
  } catch (error) {
    console.error('Error handling player reconnect:', error);
    return null;
  }
}

/**
 * Verify game state integrity
 * Checks for inconsistencies in game state
 */
export async function verifyGameStateIntegrity(roomId) {
  try {
    const roomSnapshot = await getDocs(
      query(collection(db, 'rooms'), where('__name__', '==', roomId))
    );

    if (roomSnapshot.empty) return { valid: false };

    const roomData = roomSnapshot.docs[0].data();
    const issues = [];

    // Check: validators and participants don't overlap
    const minerSet = new Set(roomData.miners || []);
    const participantSet = new Set(roomData.participants || []);

    const overlap = [...minerSet].filter((m) => participantSet.has(m));
    if (overlap.length > 0) {
      issues.push(
        `Players in both validators and participants: ${overlap.join(', ')}`
      );
    }

    // Check: game state stage is valid
    const validStages = [
      'WAITING',
      'STAKING',
      'WINNER_DISPLAY',
      'PLAYING',
      'LEADERBOARD',
    ];
    if (!validStages.includes(roomData.gameState?.stage)) {
      issues.push(`Invalid game stage: ${roomData.gameState?.stage}`);
    }

    // Check: stageEndsAt is in future (if stage is timed)
    if (
      ['STAKING', 'WINNER_DISPLAY', 'LEADERBOARD'].includes(
        roomData.gameState?.stage
      )
    ) {
      const stageEndsAt =
        roomData.gameState?.stageEndsAt?.toDate?.() ||
        roomData.gameState?.stageEndsAt;
      const now = new Date();

      if (stageEndsAt && stageEndsAt < now) {
        issues.push(
          `Stage end time is in the past (${stageEndsAt}) vs now (${now})`
        );
      }
    }

    return {
      valid: issues.length === 0,
      issues: issues,
    };
  } catch (error) {
    console.error('Error verifying game state integrity:', error);
    return { valid: false, error: true };
  }
}
